import { z } from "zod";
import { AccountClass } from "@common/constants/domains/accounting/accounts";
import {
    CounterpartyDirection,
    CounterpartyDirectionOverride,
    RegisteredCounterpartyIncludeScope,
} from "../helpers/counterparties";
import {
    CounterpartySource,
    FederalTaxClassification,
    TinSource,
    TinType,
} from "../types/counterparty";
import {
    AmountFilterLevel,
    CounterpartyDisabledFilter,
    DirectorySort,
    MAX_COUNTERPARTY_CONTACT_FIELD_LENGTH,
    MAX_COUNTERPARTY_NOTE_LENGTH,
} from "../constants/counterparty";
import { AccountingLedgerBasis } from "../types/domains/accounting/ledger";
import { MoneyDirection } from "../constants";
import { AmountRoundingLevel } from "../types/transactionRule";
import { JournalEntrySourceFilter } from "./domains/accounting/journalEntry";
import {
    counterpartyFieldReviewSchema,
    genericCounterpartyReviewFieldEnum,
} from "./counterpartyFieldReview.schema";

// Blank input is deliberately normalized to null instead of rejected with a
// 400, so a direct API caller can clear a field the same way the form does.
const createOptionalCounterpartyTextSchema = (maxLength: number) =>
    z
        .string()
        .trim()
        .max(maxLength, `Must be ${maxLength} characters or fewer`)
        .nullable()
        .optional()
        .transform((value) => (value === "" ? null : value));

export const optionalCounterpartyTextSchema =
    createOptionalCounterpartyTextSchema(MAX_COUNTERPARTY_CONTACT_FIELD_LENGTH);

export const optionalCounterpartyEmailSchema =
    optionalCounterpartyTextSchema.pipe(
        z.string().email("Invalid email address").nullable().optional(),
    );

export const WEBSITE_MUST_BE_HTTP_URL = "Website must be an http or https URL";

// A hostname is only accepted when it has at least one dot and a real TLD, so a
// scheme-less token like `invoice` (which `new URL` would happily turn into
// `https://invoice`) is rejected instead of stored as a website.
const domainNamePattern =
    /^([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;

function isHttpWebsite(value: string): boolean {
    const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(value);
    const candidate = hasScheme ? value : `https://${value}`;
    let url: URL;
    try {
        url = new URL(candidate);
    } catch {
        return false;
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") {
        return false;
    }
    return domainNamePattern.test(url.hostname);
}

export const optionalCounterpartyWebsiteSchema =
    optionalCounterpartyTextSchema.pipe(
        z
            .string()
            .refine(isHttpWebsite, { message: WEBSITE_MUST_BE_HTTP_URL })
            .nullable()
            .optional(),
    );

export const HTML_NOT_ALLOWED_IN_NOTE = "HTML is not allowed in notes";

// Matches an actual HTML tag: `<` (optionally `/`), a tag name, optional
// attributes (which may span newlines), and an optional self-closing slash.
// Requiring whitespace or `>`/`/` right after the tag name means free-text like
// `John <john@example.com>` is left alone while `<img\nsrc=x onerror=1>` is not.
const htmlTagPattern = /<\/?[a-z][a-z0-9]*(?:\s[^>]*)?\/?>/i;

export const optionalCounterpartyNoteSchema =
    createOptionalCounterpartyTextSchema(MAX_COUNTERPARTY_NOTE_LENGTH).pipe(
        z
            .string()
            .refine((value) => !htmlTagPattern.test(value), {
                message: HTML_NOT_ALLOWED_IN_NOTE,
            })
            .nullable()
            .optional(),
    );

export const counterpartySchema = z.object({
    id: z.string(),
    name: z.string(),
    companyName: z.string().nullable(),
    workspaceId: z.string().nullable(),
    updatedByUserId: z.number().nullable(),
    updatedByUserName: z.string().nullable().optional(),
    source: z.nativeEnum(CounterpartySource).nullable(),
    sourceId: z.string().nullable(),
    email: z.string().nullable(),
    cc: z.string().nullable(),
    bcc: z.string().nullable(),
    phoneNumber: z.string().nullable(),
    website: z.string().nullable(),
    title: z.string().nullable(),
    firstName: z.string().nullable(),
    middleName: z.string().nullable(),
    lastName: z.string().nullable(),
    suffix: z.string().nullable(),
    street: z.string().nullable(),
    street2: z.string().nullable(),
    city: z.string().nullable(),
    postalCode: z.string().nullable(),
    state: z.string().nullable(),
    country: z.string().nullable(),
    shouldBeNormalized: z.boolean(),
    directionOverride: z.nativeEnum(CounterpartyDirectionOverride).nullable(),
    isEligible1099: z.boolean().nullable(),
    federalTaxClassification: z.nativeEnum(FederalTaxClassification).nullable(),
    note: z.string().nullable(),
    disabledAt: z.coerce.date().nullable(),
});

const counterpartyTinMetadataSchema = z.object({
    tinType: z.nativeEnum(TinType).nullable(),
    tinSource: z.nativeEnum(TinSource).nullable(),
    tinSourceDocumentId: z.string().uuid().nullable(),
    tinSourceNameMatch: z.boolean().nullable(),
    tinRevisionId: z.string().uuid().nullable(),
});

export const counterpartyRowSchema = counterpartySchema.merge(
    counterpartyTinMetadataSchema,
);

type CounterpartyScalarFields = z.infer<typeof counterpartySchema>;

// Legacy flat contact/address scalars. The owning data now lives in the
// `counterparty_contact` / `counterparty_address` child tables and is projected
// back onto these scalars by `CounterpartyDetailsInternalService`. They are kept
// on the shared type for backward compatibility but marked `@deprecated` below
// so new consumer code gets an editor/type-system warning.
export type CounterpartySchema = Omit<
    CounterpartyScalarFields,
    | "email"
    | "phoneNumber"
    | "title"
    | "firstName"
    | "middleName"
    | "lastName"
    | "suffix"
    | "street"
    | "street2"
    | "city"
    | "postalCode"
    | "state"
    | "country"
> & {
    /** @deprecated Legacy contact scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_contact`. */
    email: CounterpartyScalarFields["email"];
    /** @deprecated Legacy contact scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_contact`. */
    phoneNumber: CounterpartyScalarFields["phoneNumber"];
    /** @deprecated Legacy contact scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_contact`. */
    title: CounterpartyScalarFields["title"];
    /** @deprecated Legacy contact scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_contact`. */
    firstName: CounterpartyScalarFields["firstName"];
    /** @deprecated Legacy contact scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_contact`. */
    middleName: CounterpartyScalarFields["middleName"];
    /** @deprecated Legacy contact scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_contact`. */
    lastName: CounterpartyScalarFields["lastName"];
    /** @deprecated Legacy contact scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_contact`. */
    suffix: CounterpartyScalarFields["suffix"];
    /** @deprecated Legacy address scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_address`. */
    street: CounterpartyScalarFields["street"];
    /** @deprecated Legacy address scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_address`. */
    street2: CounterpartyScalarFields["street2"];
    /** @deprecated Legacy address scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_address`. */
    city: CounterpartyScalarFields["city"];
    /** @deprecated Legacy address scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_address`. */
    postalCode: CounterpartyScalarFields["postalCode"];
    /** @deprecated Legacy address scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_address`. */
    state: CounterpartyScalarFields["state"];
    /** @deprecated Legacy address scalar — write via `CounterpartyDetailsInternalService`; see `counterparty_address`. */
    country: CounterpartyScalarFields["country"];
};

export type CounterpartyRowSchema = CounterpartySchema &
    z.infer<typeof counterpartyTinMetadataSchema>;

export const accountBreakdownEntrySchema = z.object({
    accountId: z.string(),
    accountCode: z.number(),
    accountCodeText: z.string().nullable().optional(),
    accountName: z.string(),
    debit: z.number(),
    credit: z.number(),
    journalEntryLinesCount: z.number(),
});

export type AccountBreakdownEntry = z.infer<typeof accountBreakdownEntrySchema>;

export const counterpartyUsageStatsSchema = z.object({
    journalEntryLinesCount: z.number(),
    debit: z.number(),
    credit: z.number(),
    accountBreakdown: z.array(accountBreakdownEntrySchema).default([]),
    // Count of non-deleted transactions with this counterparty set as their
    // billable-expense customer (transaction.customerId = counterparty.id).
    // Powers the billable-expenses column in the transactions drawer/UI —
    // independent of whether those expenses have been billed onto an invoice
    // yet (see `hasUnbilledCustomerExpenses` for the unbilled-only check).
    billableExpenseCount: z.number(),
    // Count of invoices with this counterparty set as their customer. Powers
    // the (default-hidden) Invoices column in the counterparty directory.
    invoiceCount: z.number(),
    // Count of bills with this counterparty set as their vendor. Powers the
    // (default-hidden) Bills column in the counterparty directory.
    billCount: z.number(),
});

export const getCounterpartiesResponseRowSchema = z.object({
    counterparty: counterpartySchema,
    usageStats: counterpartyUsageStatsSchema.optional(),
    fieldReviews: z.array(counterpartyFieldReviewSchema).optional(),
});

export const noCounterpartyTotalsSchema = z.object({
    journalEntryLinesCount: z.number(),
    debit: z.number(),
    credit: z.number(),
    accountBreakdown: z.array(accountBreakdownEntrySchema).default([]),
});

export type NoCounterpartyTotals = z.infer<typeof noCounterpartyTotalsSchema>;

export const getCounterpartiesResponseSchema = z.object({
    data: z.array(getCounterpartiesResponseRowSchema),
    noCounterpartyTotals: noCounterpartyTotalsSchema.nullable().optional(),
});

export type GetCounterpartiesResponse = z.infer<
    typeof getCounterpartiesResponseSchema
>;

// GET query params arrive as strings (this endpoint does not use `jsonQuery`),
// so `z.coerce.boolean()` cannot be used — `Boolean("false")` is `true`, which
// would invert the filter. Parse the literal string values instead.
const optionalBooleanQuerySchema = z
    .union([
        z.boolean(),
        z.enum(["true", "false"]).transform((value) => value === "true"),
    ])
    .optional();

const counterpartyQueryBaseSchema = z.object({
    includeUsageStats: z.coerce.boolean().optional(),
    createdByKick: optionalBooleanQuerySchema,
    createdByUserIds: z.array(z.coerce.number().int()).max(100).optional(),
    createdBySources: z.array(z.nativeEnum(CounterpartySource)).optional(),
    direction: z.nativeEnum(CounterpartyDirection).optional(),
    startDate: z.string().date().optional(),
    endDate: z.string().date().optional(),
    entityIds: z.array(z.coerce.number().int()).optional(),
    /**
     * Sticky Form 1099 tasks snapshot matching counterparties into this ID
     * list. When present, restricts results to these IDs (additive with other
     * filters such as review status). An empty list matches nothing, so a
     * sticky task with no members renders empty rather than every counterparty.
     */
    counterpartiesIds: z.array(z.string().uuid()).optional(),
    basis: z.nativeEnum(AccountingLedgerBasis).optional(),
    coaAccountIds: z.array(z.string().uuid()).optional(),
    financialAccountIds: z.array(z.coerce.number().int()).optional(),
    sort: z.nativeEnum(DirectorySort).optional(),
    includeRegistered: z
        .nativeEnum(RegisteredCounterpartyIncludeScope)
        .optional(),
    // Restricts results to counterparties that are set as the customer on at
    // least one invoice for the workspace's entities (excluding template
    // prototype rows). Used by the Invoices page and AR Aging reports so
    // their customer filter only lists customers that actually have
    // invoices, instead of every counterparty in the workspace.
    invoiceCounterpartiesOnly: optionalBooleanQuerySchema,
    minAmount: z.coerce.number().optional(),
    maxAmount: z.coerce.number().optional(),
    amountDirection: z.nativeEnum(MoneyDirection).optional(),
    amountFilterLevel: z.nativeEnum(AmountFilterLevel).optional(),
    amountRounded: optionalBooleanQuerySchema,
    amountRoundingLevel: z.nativeEnum(AmountRoundingLevel).optional(),
    isEligible1099: optionalBooleanQuerySchema,
    shouldExcludeCreditCardJournalEntries: optionalBooleanQuerySchema,
    shouldExcludePaymentProcessorJournalEntries: optionalBooleanQuerySchema,
    includeExcludedAmounts: optionalBooleanQuerySchema,
    journalEntrySources: z
        .array(z.nativeEnum(JournalEntrySourceFilter))
        .optional(),
    accountClasses: z.array(z.nativeEnum(AccountClass)).optional(),
    counterpartyReviewedFields: z
        .array(genericCounterpartyReviewFieldEnum)
        .optional(),
    counterpartyReviewedFieldsMode: z.enum(["any", "every"]).optional(),
    counterpartyUnreviewedFields: z
        .array(genericCounterpartyReviewFieldEnum)
        .optional(),
    counterpartyUnreviewedFieldsMode: z.enum(["any", "every"]).optional(),
    counterpartyReviewerIds: z.array(z.coerce.number().int()).optional(),
    counterpartyReviewerMode: z.enum(["all", "any", "none"]).optional(),
    disabledFilter: z.nativeEnum(CounterpartyDisabledFilter).optional(),
    excludedFederalTaxClassifications: z
        .array(z.nativeEnum(FederalTaxClassification))
        .optional(),
    excludeForeignAddresses: optionalBooleanQuerySchema,
    excludeRecognizedCorporations: optionalBooleanQuerySchema,
});

export const getCounterpartiesQuerySchema = counterpartyQueryBaseSchema.extend({
    limit: z.coerce.number().int().optional(),
    offset: z.coerce.number().int().optional(),
    search: z.string().optional(),
});

export type GetCounterpartiesQuery = z.infer<
    typeof getCounterpartiesQuerySchema
>;

export const downloadCounterpartiesQuerySchema =
    counterpartyQueryBaseSchema.extend({
        search: z.string().optional(),
    });

// invoiceCounterpartiesOnly is only honored by the ordinary counterparty
// query path; the usage-stats list/CSV paths do not apply it. Rather than
// silently returning every used counterparty, reject the unsupported
// combination so callers get a clear error instead of wrong results. Applied
// at the contract boundary (not baked into the exported object schemas, which
// stay ZodObjects so callers can still `.omit`/`.extend` them).
export const rejectInvoiceCounterpartiesOnlyWithUsageStats = (
    data: { invoiceCounterpartiesOnly?: boolean; includeUsageStats?: boolean },
    ctx: z.RefinementCtx,
) => {
    if (data.invoiceCounterpartiesOnly && data.includeUsageStats) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message:
                "invoiceCounterpartiesOnly cannot be combined with includeUsageStats",
            path: ["invoiceCounterpartiesOnly"],
        });
    }
};

const USAGE_STATS_ONLY_EXCLUSION_FILTERS = [
    "excludeForeignAddresses",
    "excludeRecognizedCorporations",
] as const;

// The exclusion filters above are only honored by the usage-stats list/CSV
// paths. Reject them on the ordinary query path rather than silently returning
// the counterparties they should drop.
export const requireUsageStatsForExclusionFilters = (
    data: Partial<
        Record<(typeof USAGE_STATS_ONLY_EXCLUSION_FILTERS)[number], boolean>
    > & { includeUsageStats?: boolean },
    ctx: z.RefinementCtx,
) => {
    if (data.includeUsageStats) {
        return;
    }
    for (const filter of USAGE_STATS_ONLY_EXCLUSION_FILTERS) {
        if (!data[filter]) {
            continue;
        }
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${filter} requires includeUsageStats`,
            path: [filter],
        });
    }
};

export const deleteCounterpartyQuerySchema = z.object({
    force: z.literal("true").optional(),
    disableInsteadOfDelete: z.literal("true").optional(),
});

export const deleteCounterpartyConflictResponseSchema = z.object({
    transactionsFound: z.boolean().optional(),
    lockedTransactionsFound: z.boolean().optional(),
    // Returned when the counterparty is still referenced by a bill or
    // invoice. Direct deletion is blocked in that case; the caller can
    // retry with disableInsteadOfDelete to archive it instead.
    billOrInvoiceLinksFound: z.boolean().optional(),
});

export const mergeCounterpartiesBodySchema = z.object({
    counterpartyToMergeId: z.string().uuid(),
    counterpartyToKeepId: z.string().uuid(),
    disableCounterpartyToMerge: z.boolean().optional(),
});

export const mergeCounterpartiesConflictResponseSchema = z.object({
    lockedTransactionsFound: z.boolean().optional(),
    linkedDataFound: z.boolean().optional(),
});
